// ✅ src/pages/Asset/MakeCapacityMaster.jsx
// FULL FILE - API INTEGRATED (ADD + EDIT + AUTOCODE + SAVE)
// Backend mount:
//   app.use("/api/makeListing", require("./routes/makeListing.routes"));
//
// APIs used:
//   GET  http://nissi-india.com/api/makeListing/code
//   GET  http://nissi-india.com/api/makeListing/getById/:id
//   POST http://nissi-india.com/api/makeListing/save
//
// Behavior:
// - If URL has ?id=123 => Edit mode (loads data)
// - Else => Add mode (auto code from /code)
// - Save => calls /save (insert/update based on makeId)
// - Add New => resets form & loads new code
// - After save => redirects to listing route (/Asset/MakeMasterListing)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaSave } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/makeMaster";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const MakeCapacityMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const query = useQuery();
  const editId = query.get("id");
  const isEdit = Boolean(editId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    makeId: 0,
    makeName: "",
    makeCode: "",
    remarks: "",
    isActive: true,
  });

  // ✅ localStorage session values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const loginUserId = Number(userId || 0);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;
  const requiredClass = "text-red-500";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const loadCodeForNew = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/code`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to generate code");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        makeCode: res.data.makeCode || "MAK/1",
      }));
    } catch (err) {
      console.error("make code load error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to generate make code"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadEditData = async (id) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/getById/${id}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load record");
        return;
      }

      const d = res.data.data || {};
      setFormData({
        makeId: d.makeId || Number(id),
        makeName: d.makeName || "",
        makeCode: d.makeCode || "",
        remarks: d.remarks || "",
        isActive: d.isActive !== undefined ? Boolean(d.isActive) : true,
      });
    } catch (err) {
      console.error("make edit load error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load make details"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadEditData(editId);
    } else {
      setFormData({
        makeId: 0,
        makeName: "",
        makeCode: "",
        remarks: "",
        isActive: true,
      });
      loadCodeForNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const validate = () => {
    if (!formData.makeName.trim()) {
      toast.error("Make Name is required!");
      return false;
    }
    if (!formData.makeCode.trim()) {
      toast.error("Make Code is required!");
      return false;
    }
    if (!loginUserId) {
      toast.error("Login User Id not found. Please login again.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const payload = {
        makeId: isEdit ? Number(formData.makeId || editId) : 0,
        makeCode: formData.makeCode,
        makeName: formData.makeName,
        remarks: formData.remarks,
        isActive: !!formData.isActive,
        loginUserId,
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved successfully!");
      navigate("/Asset/MakeMasterListing");
    } catch (err) {
      console.error("make save error:", err);
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = async () => {
    setFormData({
      makeId: 0,
      makeName: "",
      makeCode: "",
      remarks: "",
      isActive: true,
    });
    await loadCodeForNew();
    toast.info("Form reset for new entry");
  };

  const handleBack = () => navigate(-1);

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MAKE / CAPACITY MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {isEdit ? "Edit Make / Capacity" : "Make / Capacity Master"}
            </h1>

            <button
              onClick={handleBack}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? `${buttonClass} ${hoverClass} text-white`
                  : `${buttonClass} ${hoverClass} text-white`
              }`}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <div
            className={`rounded-xl border p-6 ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Make Name <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="makeName"
                  value={formData.makeName}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter make name"
                  disabled={loading || saving}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Make Code
                </label>
                <input
                  type="text"
                  name="makeCode"
                  value={formData.makeCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter make code"
                  disabled={loading || saving || !isEdit}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <label className={`${labelClass} text-right md:text-right pt-2`}>
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows={4}
                  className={inputClass}
                  placeholder="Enter remarks"
                  disabled={loading || saving}
                />
              </div>

              <div className="border-t pt-6 mt-6 border-gray-200 dark:border-gray-700">
                <div className="flex justify-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      disabled={loading || saving}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      IsActive
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-center gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                               <button
                  onClick={handleSave}
                  disabled={saving || loading}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving || loading
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : isDark
                      ? "bg-blue-700 hover:bg-blue-600 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  <FaSave />
                  {saving ? (isEdit ? "Updating..." : "Saving...") : isEdit ? "Update" : "Save"}
                </button>

                
                <button
                  onClick={handleAddNew}
                  disabled={saving || loading}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving || loading
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : isDark
                      ? "bg-green-700 hover:bg-green-600 text-white"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>

              {loading ? (
                <div className={`text-center text-sm ${isDark ? "text-white/80" : "text-gray-600"}`}>
                  Loading...
                </div>
              ) : null}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default MakeCapacityMaster;